/**
 * Habit streak milestones (app constants — locked, `docs/plans/core-gamification.plan.md`).
 * Not a DB table: `HabitAssignment.currentStreak` / `longestStreak` fire this bus on period tick.
 *
 * Locked thresholds:
 *   DAY:   7, 30, 100, 365, then n×365 (n≥2)
 *   WEEK:  4, 12, 26, 52,  then n×52  (n≥2)
 *   MONTH: 3, 6, 12,       then n×12  (n≥2)
 *
 * Note: for every unit the last "base" rung equals the repeat unit itself, so "n×repeatUnit
 * with n≥2" is exactly every further multiple of that last rung — no separate constant needed.
 */

export type HabitCadenceUnit = "DAY" | "WEEK" | "MONTH";

export const STREAK_MILESTONES_BY_UNIT: Record<HabitCadenceUnit, readonly number[]> = {
	DAY: [7, 30, 100, 365],
	WEEK: [4, 12, 26, 52],
	MONTH: [3, 6, 12],
};

function normalizeStreak(streak: number): number {
	if (!Number.isFinite(streak)) {
		return 0;
	}
	return Math.max(0, Math.floor(streak));
}

function repeatUnitFor(unit: HabitCadenceUnit): number {
	const base = STREAK_MILESTONES_BY_UNIT[unit];
	return base[base.length - 1] as number;
}

/** True when `streak` lands exactly on a base rung or a post-base `n × repeatUnit` (n≥2) rung. */
export function isStreakMilestone(unit: HabitCadenceUnit, streak: number): boolean {
	const normalized = normalizeStreak(streak);
	if (normalized <= 0) {
		return false;
	}
	const base = STREAK_MILESTONES_BY_UNIT[unit];
	if (base.includes(normalized)) {
		return true;
	}
	const repeatUnit = repeatUnitFor(unit);
	return normalized > repeatUnit && normalized % repeatUnit === 0;
}

/** Smallest milestone strictly greater than `streak` for this cadence unit. */
export function nextStreakMilestone(unit: HabitCadenceUnit, streak: number): number {
	const normalized = normalizeStreak(streak);
	const base = STREAK_MILESTONES_BY_UNIT[unit];
	for (const milestone of base) {
		if (milestone > normalized) {
			return milestone;
		}
	}
	const repeatUnit = repeatUnitFor(unit);
	const n = Math.max(2, Math.floor(normalized / repeatUnit) + 1);
	return repeatUnit * n;
}
